import React from 'react';
import { Link } from 'react-router-dom';
import {
  Swords, Users, Zap, Home, Heart, Shield,
  BarChart3, Trophy, Star, BookOpen, Package,
  Database, Wind, Store, Palette, Snowflake,
  Clock, ArrowRight, ChevronRight, Play, MapPin, TrendingUp,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageWrapper from '../components/PageWrapper';
import SEOMeta from '../components/SEOMeta';

/* ─────────────────────────────────────────────
   PILARES
   ───────────────────────────────────────────── */
const SCENES = [
  {
    id: 'warbands',
    idx: '01',
    pill: 'EL CORAZÓN DEL SERVIDOR',
    icon: Users,
    eyebrow: 'PILAR · WARBAND',
    title: 'Tu grupo, tus reglas.',
    desc: (
      <>
        Crea tanques, sanadores y DPS con sus propios talentos, glifos y equipo.
        Invócalos al instante para mazmorras, raids o Mythic+. No esperas grupo:{' '}
        <span className="cyan-highlight">tú eres el grupo</span>.
      </>
    ),
    img: '/sistemas/warbands.jpg',
    href: null,
    feats: [
      { icon: Users,    name: 'IA personalizable',      desc: 'Cada compañero tiene su rol, talentos, glifos y rotación de combate.' },
      { icon: Trophy,   name: 'Botín x2 para Warband',  desc: 'El doble de drops de bosses para equipar a tu grupo entero a la vez.' },
      { icon: Package,  name: 'Loot en área',           desc: 'Recoge automáticamente todo lo cercano de un solo gesto.' },
      { icon: TrendingUp,name: 'Escala contigo',        desc: 'Mejora el equipo de tu héroe y toda tu Warband sube de poder a tu lado.' },
    ],
  },
  {
    id: 'mythic',
    idx: '02',
    pill: 'DESAFÍO INFINITO',
    icon: Zap,
    eyebrow: 'PILAR · MYTHIC+',
    title: 'Donde el endgame nunca termina.',
    desc: (
      <>
        Mazmorras escalables con afijos rotativos que redefinen el combate.
        Cada nivel sube la dificultad y el botín. Compite contra tu mejor tiempo
        o contra el resto del servidor.
      </>
    ),
    img: '/sistemas/mythic-plus.jpg',
    href: '/jugabilidad/mythic-plus',
    feats: [
      { icon: Zap,    name: 'Afijos semanales',              desc: 'Cada semana cambia cómo enfrentas a los bosses.' },
      { icon: Clock,  name: 'Carrera contra el tiempo',      desc: 'Termina dentro del límite para subir tu keystone.' },
      { icon: Star,   name: 'Recompensas únicas',            desc: 'Equipo de Mythic+ y recompensas variadas.' },
      { icon: MapPin, name: 'NPC en las ciudades principales', desc: 'NPC disponible en Orgrimmar, Ventormenta y Dalaran.' },
    ],
  },
  {
    id: 'housing',
    idx: '03',
    pill: 'UN HOGAR PERSISTENTE',
    icon: Home,
    eyebrow: 'PILAR · HOUSING',
    title: 'Tu base. Tu Warband. Tu refugio.',
    desc: (
      <>
        Una vivienda persistente en Azeroth para tu cuenta entera. Decora,
        colecciona, comparte con tu Warband. Gestiónala desde cualquier personaje.
      </>
    ),
    img: '/sistemas/housing.jpg',
    href: '/jugabilidad/housing',
    feats: [
      { icon: Shield,   name: 'Sincronizado por guild', desc: 'Mismo hogar accesible desde todos tus personajes.' },
      { icon: Users,    name: 'NPCs útiles',            desc: 'Decenas de NPCs, portales y diferentes herramientas para manejar tu Warband.' },
      { icon: Database, name: 'Banco compartido',       desc: 'Materiales accesibles para tu Warband entera.' },
    ],
  },
];

/* ─────────────────────────────────────────────
   JOURNEY
   ───────────────────────────────────────────── */
const JOURNEY = [
  {
    id: 'configuracion',
    icon: Play,
    pill: 'PRIMER PASO · CONFIGURACIÓN',
    title: 'El servidor que se adapta a ti, no al revés.',
    desc: 'Una vez entres a Frostbound, elige tu velocidad de EXP del x1 al x7. Horda y Alianza comparten grupo sin restricciones. Los hechizos se aprenden automáticamente. Sin grind obligatorio, sin reglas arbitrarias.',
    feats: [
      { icon: BarChart3, name: 'EXP del x1 al x7',        desc: 'Cambiable cuando quieras con ".xp set X".' },
      { icon: Users,     name: 'Crossfaction',             desc: 'Horda y Alianza juntos en el mismo grupo desde el día uno.' },
      { icon: BookOpen,  name: 'Hechizos automáticos',     desc: 'Sin visitar entrenadores al subir de nivel.' },
      { icon: Package,   name: 'Loot en área',             desc: 'Todo el botín cercano recogido de un solo gesto.' },
    ],
  },
  {
    id: 'warband',
    icon: Users,
    pill: 'WARBAND · ARMA TU GRUPO',
    title: 'Tu cuenta es tu ejército. Tú eliges la composición.',
    desc: 'Crea varios personajes: el tanque, el sanador, el gnomo que nadie quiere jugar. Asígnale sus talentos, glifos y estrategias. Invócalo como compañero permanente. Desde este momento nunca volverás a esperar grupo para entrar a una mazmorra. Cambia de personaje con solo hacer login.',
    feats: [
      { icon: Users,    name: 'Compañeros permanentes',   desc: 'Tus Alts invocables con sus propios talentos y rol.' },
      { icon: Shield,   name: 'Mercenarios de relleno',   desc: 'Bots aleatorios de ilvl garantizado para rellenar huecos de raid.' },
      { icon: Trophy,   name: 'Botín x2 para la Warband', desc: 'El doble de drops de bosses para equipar a todo el grupo a la vez.' },
      { icon: Database, name: 'Banco compartido',         desc: 'Materiales de cuenta accesibles desde cualquier personaje.' },
    ],
  },
  {
    id: 'leveling',
    icon: Swords,
    pill: 'LEVELING · A TU RITMO',
    title: 'Misiones, mazmorras, profesiones. Nunca bloqueado.',
    desc: 'Sube como quieras: quests, mazmorras en grupo o solo con tu Warband. Disfruta de todo el contenido sin colas y a tu ritmo.',
    feats: [
      { icon: Swords,    name: 'Mazmorras sin esperar grupo', desc: 'Tu Warband rellena cualquier rol que falte.' },
      { icon: BarChart3, name: 'Profesiones aumentadas',      desc: 'Materiales de profesión y habilidad x2.' },
      { icon: Heart,     name: 'Recompensas cada 10 niveles', desc: 'Cosméticas exclusivas por cada hito de progresión.' },
      { icon: Wind,      name: 'Vuelos instantáneos',         desc: 'Sin tiempos muertos entre zona y zona.' },
    ],
  },
  {
    id: 'endgame',
    icon: Zap,
    pill: 'ENDGAME · LOBO SOLITARIO',
    title: 'Naxxramas, ToC, ICC. Con tu Warband o con otros jugadores.',
    desc: 'Toda la progresión de WotLK accesible en solitario. Entra en las raids, dirige el grupo, llévate el botín. Mythic+ para los que buscan desafío semanal. Marca Helada como moneda de progresión que no tiene atajo de pago. El nivel 80 no es el final, es donde empieza Frostbound.',
    feats: [
      { icon: Swords,   name: 'Raids con Warband',   desc: 'Naxx, Ulduar, ToC e ICC accesibles sin depender de pug.' },
      { icon: Zap,      name: 'Mythic+ (7 niveles)', desc: 'Afijos semanales con recompensas escalables y keystone propio.' },
      { icon: Star,     name: 'Marca Helada',        desc: 'Divisa que dropea en ICC y ToC. Acelerador de progresión, no p2w.' },
      { icon: Users,    name: 'Co-Op opcional',      desc: '2–3 jugadores reales pueden combinar sus Warbands para completar cualquier raid de 25 jugadores.' },
      { icon: TrendingUp, name: 'Tu progreso es el techo', desc: 'Cuanto mejor tu equipo, más fuerte entra tu Warband a las raids.' },
    ],
  },
];

/* ─────────────────────────────────────────────
   QoL GRID
   ───────────────────────────────────────────── */
const QOL_FEATURES = [
  { icon: Star,     name: 'Cosméticos cada 10 niveles', desc: 'Títulos, tinturas y monturas exclusivas por cada hito de progresión.' },
  { icon: Wind,     name: 'Monturas compartidas',       desc: 'Una montura ganada en cualquier personaje, disponible en todos.' },
  { icon: Trophy,   name: 'Logros de cuenta',           desc: 'Los logros se sincronizan entre todos tus personajes.' },
  { icon: Palette,  name: 'Transfiguración',            desc: 'Personaliza el aspecto de tu equipo sin sacrificar estadísticas.' },
  { icon: Store,    name: 'Subasta 24/7',               desc: 'AH Bot activo. La economía nunca se detiene aunque no haya jugadores.' },
  { icon: Clock,    name: 'Recompensas por tiempo',     desc: 'Cada sesión deja algo nuevo, aunque solo juegues una hora.' },
  { icon: Heart,    name: 'NPC de mascotas',            desc: 'Gestiona y personaliza las mascotas de tu cazador desde cualquier zona.' },
  { icon: Package,  name: 'Reliquias gratuitas',        desc: 'NPC con reliquias gratis disponible en todas las zonas de inicio.' },
];

/* ─────────────────────────────────────────────
   Componentes
   ───────────────────────────────────────────── */

function Scene({ scene, reverse, index }) {
  const Icon = scene.icon;
  const cta = scene.href ? (
    <Link to={scene.href} className="jp-scene-cta" data-testid={`scene-cta-${scene.id}`}>
      EXPLORAR <ChevronRight size={12} />
    </Link>
  ) : null;

  return (
    <article className={`jp-scene${reverse ? ' jp-scene--reverse' : ''} anim-fade-up`} style={{ animationDelay: `${0.1 + index * 0.05}s` }}>
      <div className="jp-scene-vis">
        <img src={scene.img} alt="" aria-hidden="true" loading="lazy" />
        <div className="jp-scene-vis-grad" />
        <span className="jp-scene-num">{scene.idx}</span>
        <span className="jp-scene-pill">{scene.pill}</span>
      </div>
      <div className="jp-scene-body">
        <div className="jp-eyebrow">
          <Icon size={14} />
          <span>{scene.eyebrow}</span>
        </div>
        <h2 className="jp-scene-title">{scene.title}</h2>
        <p className="jp-scene-desc">{scene.desc}</p>
        <div className="jp-scene-feats">
          {scene.feats.map((f) => {
            const FIcon = f.icon;
            return (
              <div key={f.name} className="jp-feat">
                <div className="jp-feat-icon"><FIcon size={16} /></div>
                <div>
                  <div className="jp-feat-name">{f.name}</div>
                  <div className="jp-feat-desc">{f.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
        {cta && <div className="jp-scene-cta-wrap">{cta}</div>}
      </div>
    </article>
  );
}

function JourneyStage({ stage, index }) {
  const Icon = stage.icon;
  return (
    <div className="jp-stage anim-fade-up" style={{ animationDelay: `${0.1 + index * 0.05}s` }}>
      <div className="jp-stage-node">
        <Icon size={16} />
      </div>
      <div className="jp-stage-body">
        <span className="jp-stage-pill">{stage.pill}</span>
        <h3 className="jp-stage-title">{stage.title}</h3>
        <p className="jp-stage-desc">{stage.desc}</p>
        <div className="jp-stage-feats">
          {stage.feats.map((f) => {
            const FIcon = f.icon;
            return (
              <div key={f.name} className="jp-stage-feat">
                <FIcon size={15} className="jp-stage-feat-icon" />
                <div>
                  <div className="jp-stage-feat-name">{f.name}</div>
                  <div className="jp-stage-feat-desc">{f.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function SistemasPage() {
  return (
    <PageWrapper scrollable>
      <SEOMeta
        title="Jugabilidad | Funcionalidades de Frostbound WotLK"
        description="Descubre toda la jugabilidad de Frostbound: Warbands, Mythic+, Housing, progresión flexible, calidad de vida y más. Servidor WoW WotLK 3.3.5a."
        canonical="/jugabilidad"
      />
      <Navbar />

      <main className="jp-main">

        {/* HERO */}
        <section className="jp-hero anim-fade-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="jp-hero-title">
            <Swords size={28} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 12, color: '#00e5ff' }} />
            Jugabilidad.
          </h1>
          <p className="jp-hero-sub">
            Tres pilares y +25 mejoras de calidad de vida que reinventan
            Rasganorte para jugarlo solo, en cooperativo o con tu propia Warband.
          </p>
          <div className="jp-hero-stats">
            <div className="jp-hero-stat"><b>25+</b><span>Mejoras QoL</span></div>
            <div className="jp-hero-stat"><b>x1–7</b><span>EXP ajustable</span></div>
            <div className="jp-hero-stat"><b>3.3.5a</b><span>Cliente</span></div>
          </div>
        </section>

        {/* PILARES */}
        <section className="jp-scenes">
          {SCENES.map((scene, i) => (
            <Scene key={scene.id} scene={scene} reverse={i % 2 === 1} index={i} />
          ))}
        </section>

        {/* JOURNEY TIMELINE */}
        <section className="jp-journey anim-fade-up" style={{ animationDelay: '0.2s' }}>
          <div className="jp-journey-head">
            <div className="jp-eyebrow jp-eyebrow--center">
              <MapPin size={14} />
              <span>DEL NIVEL 1 AL ENDGAME</span>
            </div>
            <h2 className="jp-journey-title">
              Tu camino en Frostbound, <span className="cyan-highlight">paso a paso</span>.
            </h2>
          </div>

          <div className="jp-journey-rail">
            {JOURNEY.map((stage, i) => (
              <JourneyStage key={stage.id} stage={stage} index={i} />
            ))}
          </div>
        </section>

        {/* QoL GRID */}
        <section className="jp-qol anim-fade-up" style={{ animationDelay: '0.25s' }}>
          <div className="jp-qol-head">
            <h2 className="jp-journey-title">
              Diseñado para que <span className="cyan-highlight">cada sesión valga</span>.
            </h2>
          </div>
          <div className="jp-qol-grid">
            {QOL_FEATURES.map((f) => {
              const FIcon = f.icon;
              return (
                <div key={f.name} className="jp-qol-card">
                  <div className="jp-qol-card-icon"><FIcon size={20} /></div>
                  <div className="jp-qol-card-name">{f.name}</div>
                  <div className="jp-qol-card-desc">{f.desc}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA BAR */}
        <div className="jp-cta-bar anim-fade-up" style={{ animationDelay: '0.3s' }}>
          <Snowflake size={18} className="jp-cta-deco" />
          <div className="jp-cta-text-wrap">
            <span className="jp-cta-eyebrow">EL CAMINO EMPIEZA HOY</span>
            <span className="jp-cta-text">Tu Warband te está esperando en Rasganorte.</span>
          </div>
          <a href="/descargas" className="cta-button" style={{ gap: 8, whiteSpace: 'nowrap' }}>
            JUGAR AHORA <ArrowRight size={14} />
          </a>
          <Snowflake size={18} className="jp-cta-deco" />
        </div>

      </main>

      <Footer />
    </PageWrapper>
  );
}
